// KK Marketing Hub — Dashboard Pages
// นิยามเมนู sidebar แบบจัดหมวด (2026-07-24 IA restructure) — กรองตาม role ใน AppShell
//
// Source of truth:
// - Nav ที่แสดงจริง = NAV_SECTIONS ด้านล่างไฟล์นี้
// - Route ทั้งหมด (รวมหน้าที่ไม่อยู่ใน sidebar เช่น /xian/vs, /players/:id) = packages/admin/src/App.tsx
// - แผนที่หน้า + สิทธิ์ + แหล่งข้อมูลต่อหน้า = docs/ARCHITECTURE.md §8
//
// ดัชนีหน้าปัจจุบัน (route -> แหล่งข้อมูลหลัก):
//   หน้าข้อมูล (role "data" = admin / manager / marketing ที่มีทีมหรือมีสิทธิ์ดูทุกทีม · กติกา lib/dataAccess.ts)
//   role user / marketing ไม่มีทีม ไม่เห็นเมนูเหล่านี้ และเข้า URL ตรงก็เจอหน้าแจ้งไม่มีสิทธิ์ (RequireDataAccess):
//     /               ภาพรวม            GET /api/dashboard/overview
//     /xian           เชียน             GET /api/dashboard/xian
//     /xian/vs        เทียบเชียน        (ข้อมูลเดียวกับ /xian, เข้าผ่าน tab — ไม่อยู่ใน sidebar)
//     /xian/daily*    (ลบ 2026-08-23 — เลิกใช้ รอหน้าการเงินใหม่ · redirect ไป /xian · API+ข้อมูลยังอยู่)
//     /players        ลูกค้า            GET /api/dashboard/players
//     /players/:id    ลูกค้ารายตัว       (เข้าจากคลิกแถว — ไม่อยู่ใน sidebar)
//     /referrers      ผู้แนะนำ           inviter/referrer (report11)
//     /referrers/domains  วิเคราะห์โดเมน/ref  (อดีต /domains — เข้าผ่าน tab ใต้ผู้แนะนำ)
//     /games          เกม               GET /api/dashboard/games
//   Marketing ขึ้นไป (role "marketing"):
//     /ad-accounts    บัญชีโฆษณา        GET /api/ad-accounts*
//   ทุก role ที่ /api/accounting/me อนุญาต (role "all" + gate showAdLabNav ใน AppShell · รวม role user):
//     /ad-accounts2   บัญชีโฆษณา 2      GET/PUT /api/accounting/*
//   Admin เท่านั้น (role "admin"):
//     /sync                   Sync Log        GET /api/sync/*
//     /marketing              ข้อมูล 168      GET /api/dashboard/marketing/*
//     /admin/users            จัดการผู้ใช้     GET/POST /api/admin/users
//     /admin/telegram-reports รายงาน Telegram GET/POST /api/admin/telegram-reports
//     /settings               ตั้งค่า          AI models, Cloudflare verify/usage
//     /settings/statuses      จัดการสถานะ     /api/ad-status-options
//     /settings/team-targets  เป้าหมายรายเดือน /api/admin/team-brand-targets
//
// ถอดออกจากระบบ (2026-07-24):
//   /salepages* (จัดการเซลเพจ) — ซ้ำกับระบบ kkautoweb; ลบหน้า UI แล้ว (API + D1 ยังอยู่)
//   /team (ช่องทาง) — data ก้อนเดียวกับ /xian แค่ group ตามทีม; scoreboard ซ้ำกับ /ads
//   /domains — ย้ายเป็น tab ใต้ /referrers (เนื้อหาเป็นเรื่อง ref/ชวนเพื่อน ไม่ใช่โดเมนโฆษณา)
//   /ads (โฆษณา) — ถอด 2026-07-25: วิเคราะห์จาก channel ที่เซียนตั้งเอง กำกวม/ผิดง่าย; channelToTeam() เดาทีมไม่แม่น

/** all = ทุกคน · data = คนที่ดูข้อมูลได้ (dataAccessOf != "none") · marketing = admin + marketing · admin = admin */
export type NavRole = "all" | "data" | "marketing" | "admin" | "leadOnly"; // leadOnly = หัวหน้าทีมที่ไม่ใช่ admin (admin เห็นเมนูเดียวกันใต้ ตั้งค่า อยู่แล้ว)

export type NavChild = {
  path: string;
  label: string;
  role: NavRole;
};

export type NavItem = {
  path: string;
  label: string;
  icon: string;
  /** สีไอคอนประจำเมนู (Tailwind text-*) — สีเดียวตามฟังก์ชันไปทุกที่ ให้จำเมนูด้วยสีได้ (แบบ kkautoweb) */
  tint: string;
  role: NavRole;
  /** มีลูก = เมนูแม่กางได้ (path ของแม่ = ลูกตัวแรก ใช้ตอน sidebar ย่อ) */
  children?: NavChild[];
};

export type NavSection = {
  /** หัวหมวดใน sidebar — ไม่ใส่ = หมวดบนสุด (ไม่มีหัวข้อ) */
  title?: string;
  items: NavItem[];
};

export const NAV_SECTIONS: NavSection[] = [
  {
    // ใช้ทุกวัน — ปักบนสุด กดถึงในคลิกเดียว
    // (รายงานรายวัน /xian/daily ถูกลบ 2026-08-23 — เลิกใช้ รอหน้าการเงินรูปแบบใหม่)
    items: [
      { path: "/", label: "ภาพรวม", icon: "LayoutDashboard", tint: "text-slate-500", role: "data" },
    ],
  },
  {
    title: "วิเคราะห์",
    items: [
      { path: "/xian", label: "เชียน", icon: "Users", tint: "text-brand-600", role: "data" },
      { path: "/players", label: "ลูกค้า", icon: "UserCheck", tint: "text-emerald-600", role: "data" },
      { path: "/games", label: "เกม", icon: "Gamepad2", tint: "text-violet-600", role: "data" },
      { path: "/referrers", label: "ผู้แนะนำ", icon: "Award", tint: "text-amber-600", role: "data" },
    ],
  },
  {
    title: "การตลาด",
    items: [
      { path: "/ad-accounts", label: "บัญชีโฆษณา", icon: "CreditCard", tint: "text-orange-600", role: "marketing" },
      // บัญชีโฆษณา 2 (Team KK) — ระบบ AccountingPlan ฝัง iframe · gate Team KK ที่ API/หน้าอีกชั้น
      {
        path: "/ad-accounts2",
        label: "บัญชีโฆษณา 2",
        icon: "CreditCard",
        tint: "text-pink-600",
        role: "all",
        children: [
          { path: "/ad-accounts2", label: "ภาพรวม", role: "all" },
          { path: "/ad-accounts2/emails", label: "รายชื่อเมล", role: "all" },
          { path: "/ad-accounts2/cards", label: "คลังบัตร", role: "all" },
          { path: "/ad-accounts2/budget", label: "บัญชี", role: "all" },
          { path: "/ad-accounts2/xians", label: "เซียน", role: "all" },
          { path: "/ad-accounts2/keywords", label: "คีย์เวิร์ด", role: "all" },
          { path: "/ad-accounts2/companies", label: "บริษัท", role: "all" },
          { path: "/ad-accounts2/settings", label: "ตั้งค่า", role: "all" },
        ],
      },
    ],
  },
  {
    title: "หลังบ้าน",
    items: [
      {
        path: "/sync",
        label: "ข้อมูลระบบ",
        icon: "Database",
        tint: "text-slate-500",
        role: "admin",
        children: [
          { path: "/sync", label: "Sync Log", role: "admin" },
          { path: "/marketing", label: "ข้อมูล 168", role: "admin" },
        ],
      },
      { path: "/admin/telegram-reports", label: "รายงาน Telegram", icon: "Send", tint: "text-sky-600", role: "admin" },
      { path: "/admin/users", label: "จัดการผู้ใช้", icon: "UserCog", tint: "text-teal-600", role: "admin" },
      // หัวหน้าทีมจัดการสถานะได้เท่า admin (เจ้าของระบบ 2026-10-06) · admin เห็นใต้ ตั้งค่า (เมนูแม่ /settings เป็นของ admin)
      { path: "/settings/statuses", label: "จัดการสถานะ", icon: "Settings", tint: "text-slate-500", role: "leadOnly" },
      {
        path: "/settings",
        label: "ตั้งค่า",
        icon: "Settings",
        tint: "text-slate-500",
        role: "admin",
        children: [
          { path: "/settings", label: "ตั้งค่าระบบ", role: "admin" },
          { path: "/settings/statuses", label: "จัดการสถานะ", role: "admin" },
          { path: "/settings/team-targets", label: "เป้าหมายรายเดือน", role: "admin" },
        ],
      },
    ],
  },
];
